import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, UserMinus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { LIQUIDATION_STATUS_BADGE_VARIANT } from "@/components/payroll/liquidation-format";
import { useSettlements } from "@/lib/api/payroll-settlements.queries";
import { usePayrollEmployees } from "@/lib/api/payroll-employees.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { SettlementStatusFilter } from "@/lib/api/payroll-settlements";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/definitivas/",
)({
  component: SettlementsIndexPage,
});

const STATUS_FILTERS: SettlementStatusFilter[] = [
  "all",
  "draft",
  "confirmed",
  "void",
];

function SettlementsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SettlementsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function SettlementsIndexContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [status, setStatus] = useState<SettlementStatusFilter>("all");
  const [page, setPage] = useState(1);

  const employeesQuery = usePayrollEmployees(companyId, {
    status: "all",
    pageSize: 200,
  });
  const employeeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employeesQuery.data?.employees ?? []) {
      map.set(e.id, `${e.firstName} ${e.lastName}`);
    }
    return map;
  }, [employeesQuery.data]);

  const filters = useMemo(
    () => ({ status, page, pageSize: 50 }),
    [status, page],
  );
  const listQuery = useSettlements(companyId, filters);
  const settlements = listQuery.data?.settlements ?? [];
  const total = listQuery.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / 50));
  const hasFilters = status !== "all";

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settlements.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("settlements.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/nomina/definitivas/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("settlements.list.new")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 md:grid-cols-3">
          <div>
            <Label htmlFor="set-filter-status">
              {t("settlements.list.filter_status")}
            </Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as SettlementStatusFilter);
                setPage(1);
              }}
            >
              <SelectTrigger id="set-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`liquidations.statusFilter.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("settlements.list.error_title")}
          description={t("settlements.list.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : settlements.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <UserMinus className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {hasFilters
                  ? t("settlements.list.empty_search_title")
                  : t("settlements.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? t("settlements.list.empty_search_body")
                  : t("settlements.list.empty_body")}
              </p>
            </div>
            {!hasFilters ? (
              <Button asChild>
                <Link
                  to="/companies/$companyId/nomina/definitivas/new"
                  params={{ companyId }}
                >
                  <Plus className="h-4 w-4" />
                  {t("settlements.list.new")}
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settlements.table.employee")}</TableHead>
                <TableHead>{t("settlements.table.termination_date")}</TableHead>
                <TableHead>{t("settlements.table.reason")}</TableHead>
                <TableHead className="text-right">
                  {t("settlements.table.total")}
                </TableHead>
                <TableHead>{t("settlements.table.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settlements.map((settlement) => (
                <TableRow key={settlement.id}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/nomina/definitivas/$settlementId"
                      params={{ companyId, settlementId: settlement.id }}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {employeeName.get(settlement.employeeId) ??
                        settlement.employeeId}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    {settlement.terminationDate}
                  </TableCell>
                  <TableCell className="text-sm">
                    {t(`settlements.reason.${settlement.terminationReason}`)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(settlement.totalAmount)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        LIQUIDATION_STATUS_BADGE_VARIANT[settlement.status]
                      }
                    >
                      {t(`liquidations.status.${settlement.status}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("settlements.list.page_of", { page, pageCount, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t("common.back")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("common.next")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
